import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { Container, Item } from '../../../../components/Grid';
import HistoryRowControls from '../../../../components/HistoryRowControls';
import HistoryRowListing from '../../../../components/HistoryRowListing';
import HistorySection from '../../../../components/HistorySection';

import DocumentCreationButton from '../../../../components/DocumentCreationButton';
import { statementStyle } from '../Components/Statement';
import { EditingLockedContentContext } from 'Containers/EditingLockedContentContext';

const TitleValue = (title: React.JSX.Element, value: string | React.JSX.Element): React.JSX.Element => (
  <Container>
    <Item xs={10}>{title}</Item>
    <Item xs={2}>{value}</Item>
  </Container>
);

const Scoliosis = ({ documents, startEdit }: IOwnProps): React.JSX.Element => {
  const showProcedureCodeHeader = documents.some(d => d.procedureCode && d.procedureCode.length > 0);
  const showStatementHeader = documents.some(d => d.statement && d.statement.length > 0);

  const showProcedureCode = (d: IMRI) => d.procedureCode && d.procedureCode.length > 0;
  const showStatement = (d: IMRI) => d.statement && d.statement.length > 0;

  const { openEditing } = React.useContext(EditingLockedContentContext);

  return (
    <HistorySection
      headerText={<FormattedMessage id={'imaging.scoliosis'} />}
      newButton={
        <DocumentCreationButton
          name="scoliosis"
          text={'imaging.newScoliosisRontgen'}
          onClick={startEdit({} as IScoliosisRontgen, 'scoliosis')}
          width={15}
          height={3}
          fontSize={12}
          alternate={true}
        />
      }
    >
      <HistoryRowListing
        documents={documents}
        headers={
          <>
            {showProcedureCodeHeader && (
              <Item xs={2}>
                <FormattedMessage id="imaging.procedureCode" />
              </Item>
            )}
            {showStatementHeader && (
              <Item xs={3}>
                <FormattedMessage id="imaging.statementText" />
              </Item>
            )}
            <Item xs={4}>
              <FormattedMessage id="imaging.coronalProfile" />
            </Item>
            <Item xs={3}>
              <FormattedMessage id="imaging.sagittalProfile" />
            </Item>
          </>
        }
        contentFormat={(d: IScoliosisRontgen): React.JSX.Element => (
          <>
            {showProcedureCodeHeader && <Item xs={2}>{showProcedureCode(d) ? d.procedureCode : ''}</Item>}
            {showStatementHeader && (
              <Item xs={3} style={statementStyle}>
                {showStatement(d) ? d.statement : ''}
              </Item>
            )}
            <Item xs={4} id={'proximalThoracic'}>
              {TitleValue(
                <FormattedMessage id="imaging.proximalThoracicCobb" />,
                d.proximalThoracicCobb || d.proximalThoracicCobb === 0 ? `${d.proximalThoracicCobb}°` : '-',
              )}
              {TitleValue(
                <FormattedMessage id="imaging.thoracicCobb" />,
                d.thoracicCobb || d.thoracicCobb === 0 ? `${d.thoracicCobb}°` : '-',
              )}
              {TitleValue(
                <FormattedMessage id="imaging.thoracolumbarCobb" />,
                d.thoracolumbarCobb || d.thoracolumbarCobb === 0 ? `${d.thoracolumbarCobb}°` : '-',
              )}
            </Item>
            <Item xs={3}>
              {TitleValue(
                <FormattedMessage id="imaging.kyphosist2t12" />,
                d.kyphosist2t12 || d.kyphosist2t12 === 0 ? `${d.kyphosist2t12}°` : '-',
              )}
              {TitleValue(
                <FormattedMessage id="imaging.kyphosist5t12" />,
                d.kyphosist5t12 || d.kyphosist5t12 === 0 ? `${d.kyphosist5t12}°` : '-',
              )}
              {TitleValue(
                <FormattedMessage id="imaging.lordosist12s1" />,
                d.lordosist12s1 || d.lordosist12s1 === 0 ? `${d.lordosist12s1}°` : '-',
              )}
            </Item>
          </>
        )}
        makeDate
        makeControls
        historyRowControls={(d: IControlProps) => (
          <HistoryRowControls
            document={d}
            startEdit={
              d._lockedFor && d._source !== null ? doc => () => openEditing(doc, 'imaging.scoliosis') : startEdit
            }
          />
        )}
      />
    </HistorySection>
  );
};

interface IOwnProps {
  documents: IScoliosisRontgen[];
  startEdit: (document: IScoliosisRontgen, name?: string) => (e: React.MouseEvent<Element, MouseEvent>) => void;
}

export default Scoliosis;
